import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { TextInput } from 'react-native';
import { useQuery } from "@tanstack/react-query";
import { Card } from "@constants/Themed";
import { Text, YStack, XStack, Image, Button, H4, H2 } from "tamagui";
import apis, { uploadGlobalAsset } from "@api/api";
import DefaultScreen from "@components/Global/DefaultScreenView";
import HomeQuickStats from "../WelcomeScreen/Widgets/Components/SubComponents/GaugeStats";
import RowComponent from "@components/Global/RowComponent/RowComponent";
import { 
  View, 
  StyleSheet, 
  TouchableOpacity, 
  FlatList, 
  ScrollView,
  Dimensions, 
  Platform,
  ActivityIndicator,
  Pressable,
  Modal,
  KeyboardAvoidingView
} from 'react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { selectUser } from '@reduxLocal/selectors/userSelectors';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { AntDesign, MaterialIcons, Feather, MaterialCommunityIcons } from "@expo/vector-icons";
import * as ImagePicker from 'expo-image-picker';
import type { ImagePickerAsset } from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import CustomDialog from '@components/UI/CustomDialog';
import handleImageUpload from "@utils/handleImage";
import aiImageGen from "./utils/aiImageGen";
import ErrorBoundary from '@utils/ErrorBoundary';
import * as Animatable from 'react-native-animatable';
import ImageEnhancementModal from '../../LowLevel/Components/ImageEnhancementModal';

// Optimized icon components
const FileImageOutlined = React.memo(() => <AntDesign name="picture" size={20} />);
const VideoCameraOutlined = React.memo(() => <AntDesign name="video-camera" size={20} />);
const FileTextOutlined = React.memo(() => <AntDesign name="file-text" size={20} />); 

// MediaItem component to handle individual media items with proper error handling
const MediaItem = React.memo(({ item, index, itemWidth }: {
  item: any;
  index: number;
  itemWidth?: number; // Make itemWidth optional since we're using flex layout
}) => {
  const [imageLoadError, setImageLoadError] = useState(false);
  const imageUrl = typeof item === "string" ? item : item?.url;
  
  // Skip items without valid URLs
  if (!imageUrl) {
    return null;
  }

  // Extract title from various sources
  const getItemTitle = () => {
    if (typeof item === "string") {
      // Extract filename from URL
      const filename = item.substring(item.lastIndexOf('/') + 1);
      return decodeURIComponent(filename) || `Media ${index + 1}`;
    }
    return item?.title || item?.name || item?.filename || `Media ${index + 1}`;
  };

  // Format date to relative time or absolute date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
      }
      return `${diffInHours}h ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks}w ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Extract metadata
  const getMetadata = () => {
    if (typeof item === "string") {
      // Even for string URLs, try to extract basic info
      const filename = item.substring(item.lastIndexOf('/') + 1);
      const ext = filename.split('.').pop()?.toUpperCase();
      return ext ? ext : null;
    }

    const metadata = [];

    // File type/extension
    if (item?.type) {
      metadata.push(item.type.split('/')[1]?.toUpperCase() || 'FILE');
    } else if (item?.url) {
      const ext = item.url.split('.').pop()?.toUpperCase();
      if (ext) metadata.push(ext);
    }

    // File size
    if (item?.size) {
      const sizeInKB = Math.round(item.size / 1024);
      if (sizeInKB < 1024) {
        metadata.push(`${sizeInKB} KB`);
      } else if (sizeInKB < 1024 * 1024) {
        metadata.push(`${(sizeInKB / 1024).toFixed(1)} MB`);
      } else {
        metadata.push(`${(sizeInKB / (1024 * 1024)).toFixed(2)} GB`);
      }
    }

    // Dimensions
    if (item?.width && item?.height) {
      metadata.push(`${item.width}×${item.height}`);
    }

    // Duration for videos
    if (item?.duration) {
      const duration = Math.round(item.duration);
      if (duration < 60) {
        metadata.push(`${duration}s`);
      } else {
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        metadata.push(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    }

    // Upload/Created date (with relative time)
    if (item?.createdAt || item?.uploadedAt || item?.created || item?.uploaded) {
      const dateString = item.createdAt || item.uploadedAt || item.created || item.uploaded;
      metadata.push(formatDate(dateString));
    }

    // Last modified date (if different from created)
    if (item?.updatedAt || item?.modifiedAt) {
      const modifiedDate = new Date(item.updatedAt || item.modifiedAt);
      const createdDate = item?.createdAt ? new Date(item.createdAt) : null;

      // Only show if modified significantly after creation (more than 1 hour)
      if (createdDate && (modifiedDate.getTime() - createdDate.getTime() > 3600000)) {
        metadata.push(`Modified ${formatDate(item.updatedAt || item.modifiedAt)}`);
      }
    }

    // Uploader/Owner
    if (item?.uploader || item?.owner || item?.uploadedBy) {
      const uploader = item.uploader || item.owner || item.uploadedBy;
      const uploaderName = typeof uploader === 'string' ? uploader : uploader?.name || uploader?.username;
      if (uploaderName && uploaderName.length < 20) {
        metadata.push(`By ${uploaderName}`);
      }
    }

    // Tags (first tag only to save space)
    if (item?.tags && Array.isArray(item.tags) && item.tags.length > 0) {
      metadata.push(`#${item.tags[0]}`);
    }

    // View count or download count
    if (item?.views || item?.viewCount) {
      const views = item.views || item.viewCount;
      if (views > 0) {
        metadata.push(views >= 1000 ? `${(views / 1000).toFixed(1)}K views` : `${views} views`);
      }
    }

    return metadata.length > 0 ? metadata.join(' • ') : null;
  };

  // Memoize the onPress handler to prevent re-creation on every render
  const handlePress = useCallback(() => {
    console.log('Media item pressed:', imageUrl);
  }, [imageUrl]);

  // Get enhancement handler from parent if available
  const onEnhance = (item as any)?.onEnhance;

  // Memoize the onError handler
  const handleImageError = useCallback(() => {
    setImageLoadError(true);
  }, []);

  // Helper function to determine if item is an image
  const isImageFile = useCallback(() => {
    let fileUrl = '';
    let mimeType = '';
    if (typeof item === 'string') {
      fileUrl = item.toLowerCase();
    } else {
      fileUrl = (item?.url || '').toLowerCase();
      mimeType = (item?.type || '').toLowerCase();
    }
    if (mimeType && mimeType.startsWith('image/')) return true;
    const extension = fileUrl.substring(fileUrl.lastIndexOf('.') + 1);
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'heic', 'heif'];
    return imageExtensions.includes(extension);
  }, [item]);

  const title = getItemTitle();
  const metadata = getMetadata();
  const isImage = isImageFile();

  return (
    <View style={[styles.mediaItemContainer]}>
      <Card
        flex={1}
        height={280}
        onPress={handlePress}
      >
        <YStack flex={1} padding="$2">
          {!imageLoadError && imageUrl ? (
            <View style={{ position: 'relative', flex: 1 }}>
              <Image
                source={{ uri: imageUrl }}
                style={styles.mediaImage}
                resizeMode="cover"
                onError={handleImageError}
              />
              {isImage && onEnhance && (
                <TouchableOpacity
                  onPress={(e) => {
                    e.stopPropagation();
                    onEnhance(imageUrl, index);
                  }}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    borderRadius: 20,
                    padding: 8,
                    zIndex: 10,
                  }}
                >
                  <MaterialIcons name="auto-awesome" size={16} color="white" />
                </TouchableOpacity>
              )}
            </View>
          ) : (
              <YStack style={styles.mediaPlaceholder}>
              <AntDesign name="picture" size={48} color="#9ca3af" />
              <Text color="$color11" fontSize="$3" marginTop="$2">
                  Image Not Available
              </Text>
            </YStack>
          )}
          <YStack marginTop="$2" gap="$1.5">
            <Text
              fontSize="$3"
              color="$color12"
              fontWeight="600"
              numberOfLines={1}
            >
              {title}
            </Text>
            {metadata && (
              <Text
                fontSize="$1"
                color="$color10"
                numberOfLines={2}
                lineHeight={14}
              >
                {metadata}
              </Text>
            )}
          </YStack>
        </YStack>
      </Card>
    </View>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  return (
    prevProps.item === nextProps.item &&
    prevProps.index === nextProps.index
    // Removed itemWidth comparison since it's now optional and handled by parent container
  );
}); 

// YouTubeVideoItem component to handle YouTube videos
const YouTubeVideoItem = React.memo(({ item, index }: {
  item: any;
  index: number;
}) => {
  const thumbnailUrl = item?.snippet?.thumbnails?.medium?.url || item?.snippet?.thumbnails?.default?.url;
  const title = item?.snippet?.title || `Video ${index + 1}`;
  // Handle both playlistItems format (contentDetails.videoId) and search format (id.videoId)
  const videoId = item?.contentDetails?.videoId || item?.id?.videoId || item?.id;

  // Format date to relative time (same as MediaItem)
  const formatYouTubeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
  };

  // Parse YouTube duration format (PT#M#S)
  const parseYouTubeDuration = (duration: string) => {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return null;

    const hours = parseInt(match[1] || '0');
    const minutes = parseInt(match[2] || '0');
    const seconds = parseInt(match[3] || '0');

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `0:${seconds.toString().padStart(2, '0')}`;
    }
  };

  // Extract metadata
  const getMetadata = () => {
    const metadata = [];

    // View count (if available in statistics)
    if (item?.statistics?.viewCount) {
      const views = parseInt(item.statistics.viewCount);
      if (views >= 1000000) {
        metadata.push(`${(views / 1000000).toFixed(1)}M views`);
      } else if (views >= 1000) {
        metadata.push(`${(views / 1000).toFixed(1)}K views`);
      } else {
        metadata.push(`${views} views`);
      }
    }

    // Published date (relative)
    if (item?.snippet?.publishedAt) {
      metadata.push(formatYouTubeDate(item.snippet.publishedAt));
    }

    // Duration (if available)
    if (item?.contentDetails?.duration) {
      const formattedDuration = parseYouTubeDuration(item.contentDetails.duration);
      if (formattedDuration) {
        metadata.push(formattedDuration);
      }
    }

    // Like count (if available)
    if (item?.statistics?.likeCount) {
      const likes = parseInt(item.statistics.likeCount);
      if (likes >= 1000000) {
        metadata.push(`${(likes / 1000000).toFixed(1)}M likes`);
      } else if (likes >= 1000) {
        metadata.push(`${(likes / 1000).toFixed(1)}K likes`);
      }
    }

    // Comment count (if available)
    if (item?.statistics?.commentCount) {
      const comments = parseInt(item.statistics.commentCount);
      if (comments >= 1000) {
        metadata.push(`${(comments / 1000).toFixed(1)}K comments`);
      } else if (comments > 0) {
        metadata.push(`${comments} comments`);
      }
    }

    // Channel name
    if (item?.snippet?.channelTitle) {
      metadata.push(item.snippet.channelTitle);
    }

    return metadata.length > 0 ? metadata.join(' • ') : null;
  };

  const handlePress = useCallback(() => {
    console.log('YouTube video pressed:', videoId);
    // Open YouTube video in browser or native player
    const url = `https://www.youtube.com/watch?v=${videoId}`;
    if (Platform.OS === 'web') {
      window.open(url, '_blank');
    }
  }, [videoId]);

  const metadata = getMetadata();

  return (
    <View style={[styles.mediaItemContainer]}>
      <Card
        flex={1}
        height={280}
        onPress={handlePress}
      >
        <YStack flex={1} padding="$2">
          {thumbnailUrl ? (
            <Image
              source={{ uri: thumbnailUrl }}
              style={styles.mediaImage}
              resizeMode="cover"
            />
          ) : (
            <YStack style={styles.mediaPlaceholder}>
              <MaterialCommunityIcons name="youtube" size={48} color="#FF0000" />
              <Text color="$color11" fontSize="$3" marginTop="$2">
                No Thumbnail
              </Text>
            </YStack>
          )}
          <YStack marginTop="$2" gap="$1.5">
            <XStack alignItems="center" gap="$1">
              <MaterialCommunityIcons name="youtube" size={16} color="#FF0000" />
              <Text
                fontSize="$3"
                color="$color12"
                fontWeight="600"
                numberOfLines={1}
                flex={1}
              >
                {title}
              </Text>
            </XStack>
            {metadata && (
              <Text
                fontSize="$1"
                color="$color10"
                numberOfLines={2}
                lineHeight={14}
                marginLeft={20}
              >
                {metadata}
              </Text>
            )}
          </YStack>
        </YStack>
      </Card>
    </View>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.item === nextProps.item &&
    prevProps.index === nextProps.index
  );
});

const MediaScreen = () => {
  const [selectedTab, setSelectedTab] = useState<'media' | 'youtube'>('media');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month' | 'year'>('all');
  const [sizeFilter, setSizeFilter] = useState<'all' | 'small' | 'medium' | 'large'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'size' | 'name'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [glowIntensity, setGlowIntensity] = useState(0);
  const [screenData, setScreenData] = useState(() => Dimensions.get('window'));
  const animationRef = useRef<number>(0);
  const [enhancementModalVisible, setEnhancementModalVisible] = useState(false);
  const [selectedImageForEnhancement, setSelectedImageForEnhancement] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadMetadataModal, setShowUploadMetadataModal] = useState(false);
  const [pendingUploadMedia, setPendingUploadMedia] = useState<any>(null);
  const [uploadMetadata, setUploadMetadata] = useState({
    title: '',
    description: '',
    location: ''
  });
  
  const theme = themeSelector();
  const { authentications } = selectUser();
  const hasYoutubeIntegration = !!authentications?.youtube;
  const { showAlert, showConfirm } = useAlertDialog();

  // Debug logging for YouTube integration
  useEffect(() => {
    console.log('🔍 YouTube Integration Status:', {
      hasYoutubeIntegration,
      youtubeAuth: authentications?.youtube,
      allAuthentications: Object.keys(authentications || {})
    });
  }, [hasYoutubeIntegration, authentications]);

  // Optimized responsive calculations
  const { itemWidth, numColumns } = useMemo(() => {
    const minItemWidth = 240;
    const maxItemWidth = 320;
    
    const defaultScreenPadding = 30;
    const mainGap = 16;
    const sectionPadding = 32;
    const cardPadding = 32;
    const contentPadding = 32;
    const itemMargin = 16;
    
    const mediaLibraryWidthRatio = 0.65;
    const availableScreenWidth = screenData.width - defaultScreenPadding;
    const mediaLibrarySectionWidth = availableScreenWidth * mediaLibraryWidthRatio;
    const actualContentWidth = mediaLibrarySectionWidth - sectionPadding - cardPadding - contentPadding - mainGap;
    
    const gap = 0;
    let calculatedColumns = Math.max(1, Math.floor(actualContentWidth / (minItemWidth + itemMargin)));
    
    // Limit maximum columns to 3 for better readability
    if (calculatedColumns > 3) calculatedColumns = 3;
    
    const calculatedItemWidth = Math.min(
      maxItemWidth,
      Math.max(minItemWidth, (actualContentWidth - (itemMargin * calculatedColumns)) / calculatedColumns)
    );

    return {
      itemWidth: calculatedItemWidth,
      numColumns: calculatedColumns
    };
  }, [screenData.width]);

  // Listen for orientation changes with cleanup
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });

    return () => subscription?.remove();
  }, []);

  // Helper function to determine file type
  const getFileType = useCallback((item: any): 'image' | 'video' | 'document' | 'unknown' => {
    let fileUrl = '';
    let mimeType = '';

    if (typeof item === 'string') {
      fileUrl = item.toLowerCase();
    } else {
      fileUrl = (item?.url || '').toLowerCase();
      mimeType = (item?.type || '').toLowerCase();
    }

    // Check MIME type first if available
    if (mimeType) {
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('video/')) return 'video';
      if (mimeType.startsWith('application/') || mimeType.startsWith('text/')) return 'document';
    }

    // Fall back to file extension
    const extension = fileUrl.substring(fileUrl.lastIndexOf('.') + 1);

    // Image extensions
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'heic', 'heif'];
    if (imageExtensions.includes(extension)) return 'image';

    // Video extensions
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', '3gp'];
    if (videoExtensions.includes(extension)) return 'video';

    // Document extensions
    const documentExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'rtf', 'odt', 'ods'];
    if (documentExtensions.includes(extension)) return 'document';

    return 'unknown';
  }, []);

  // Handle image enhancement
  const handleEnhanceImage = useCallback((imageUrl: string, index: number) => {
    setSelectedImageForEnhancement(imageUrl);
    setSelectedImageIndex(index);
    setEnhancementModalVisible(true);
  }, []);

  // Memoized renderItem function for FlatList
  const renderItem = useCallback(({ item, index }: { item: any; index: number }) => {
    // Calculate item width for grid layout
    const gridItemWidth = numColumns > 1 ? (100 / numColumns) - (10 / numColumns) : 100;
    
    // Add enhancement handler to item if it's an image
    const itemWithEnhance = typeof item === 'object' && item?.url
      ? { ...item, onEnhance: handleEnhanceImage }
      : typeof item === 'string'
        ? { url: item, onEnhance: handleEnhanceImage }
        : item;

    return (
      <View style={{ 
        width: numColumns > 1 ? `${gridItemWidth}%` : '100%',
        flexBasis: numColumns > 1 ? `${gridItemWidth}%` : '100%',
        flexGrow: 0,
        flexShrink: 0,
        paddingHorizontal: numColumns > 1 ? 0 : 8,
        marginBottom: numColumns > 1 ? 0 : 16,
      }}>
        <MediaItem item={itemWithEnhance} index={index} itemWidth={undefined} />
      </View>
    );
  }, [numColumns, handleEnhanceImage]);

  // Media library query - must be defined before upload functions that use refetch
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['mediaLibrary'],
    queryFn: async () => {
      try {
        console.log('📁 Fetching media library with metadata...');
        const result = await apis.getGlobalAssets(true); // Explicitly request metadata

        if (!Array.isArray(result)) {
          console.warn('⚠️ Media library response is not an array:', result);
          return [];
        }

        // Log metadata presence for debugging
        console.log(`✅ Fetched ${result.length} media items`);
        if (result.length > 0) {
          const hasMetadata = result[0] && typeof result[0] === 'object' && 'size' in result[0];
          console.log(`📊 Metadata included: ${hasMetadata ? 'Yes' : 'No (URLs only)'}`);
          if (hasMetadata) {
            console.log('Sample metadata:', {
              name: result[0].name,
              size: result[0].size,
              type: result[0].type,
              createdAt: result[0].createdAt
            });
          }
        }

        return result;
      } catch (error) {
        console.error('❌ Error fetching media library:', error);
        return [];
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 15,
    refetchOnReconnect: false,
    retry: 1,
    retryDelay: 2000,
    placeholderData: [],
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  // Refetch media library when switching back to media tab
  useEffect(() => {
    if (selectedTab === 'media' && !isLoading) {
      console.log('🔄 Media tab selected, ensuring media library is loaded...');
      // Only refetch if we don't have data or if there was an error
      if (!data || (Array.isArray(data) && data.length === 0 && !error)) {
        refetch();
      }
    }
  }, [selectedTab, isLoading, data, error, refetch]);

  // Image/Media picker function with NSFW detection
  const pickImage = useCallback(async () => {
    try {
      // Use handleImageUpload which includes NSFW content detection
      const result = await handleImageUpload(true); // Pass true to get local URI (returns ImagePickerAsset)

      if (!result) {
        return null; // User cancelled or NSFW content detected
      }

      // Type guard: handleImageUpload with local=true returns ImagePickerAsset, not string
      if (typeof result === 'string') {
        console.error('Unexpected string return from handleImageUpload');
        return null;
      }

      // At this point TypeScript knows result is ImagePickerAsset
      const asset = result as ImagePickerAsset;

      // Format the asset for upload
      return {
        uri: asset.uri,
        name: asset.fileName || `media_${Date.now()}.${asset.type === 'video' ? 'mp4' : 'jpg'}`,
        type: asset.type === 'video' ? 'video/mp4' : 'image/jpeg',
      };
    } catch (error) {
      console.error('Error picking image:', error);
      await showAlert('Error', 'Failed to pick image');
      return null;
    }
  }, [showAlert]);

  // Document picker function
  const pickDocument = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        return {
          uri: asset.uri,
          name: asset.name,
          type: asset.mimeType || 'application/octet-stream',
        };
      }
      return null;
    } catch (error) {
      console.error('Error picking document:', error);
      await showAlert('Error', 'Failed to pick document');
      return null;
    }
  }, [showAlert]);

  // Upload media function
  const uploadMedia = useCallback(async (media: any, metadata?: { title?: string; description?: string; location?: string }) => {
    if (isUploading) {
      console.log('⚠️ Upload already in progress');
      return;
    }

    setIsUploading(true);
    try {
      console.log('📤 Uploading media:', media.name);

      // Convert URI to base64 data if not already present
      let base64Data: string;
      if (media.data) {
        // Data already provided (base64 string)
        base64Data = media.data;
      } else if (media.uri) {
        try {
          // Check if URI is already a data URL
          if (media.uri.startsWith('data:')) {
            base64Data = media.uri;
          } else if (media.uri.includes('base64,')) {
            base64Data = media.uri;
          } else {
            // Convert URI to base64
            if (Platform.OS === 'web') {
              // Web: fetch blob and convert to base64
              const response = await fetch(media.uri);
              const blob = await response.blob();
              base64Data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } else {
              // Native: use FileSystem
              try {
                const fileInfo = await FileSystem.getInfoAsync(media.uri);
                if (!fileInfo.exists) {
                  throw new Error('File does not exist at URI');
                }

                const base64String = await FileSystem.readAsStringAsync(media.uri, {
                  encoding: 'base64' as any,
                });

                const mimeType = media.type || 'application/octet-stream';
                base64Data = `data:${mimeType};base64,${base64String}`;
              } catch (fileSystemError) {
                console.log('FileSystem read failed, trying fetch:', fileSystemError);
                // Fallback to fetch for content:// URIs or other cases
                const response = await fetch(media.uri);
                const blob = await response.blob();
                base64Data = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result as string);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
              }
            }
          }
        } catch (conversionError) {
          console.error('❌ Error converting URI to base64:', conversionError);
          throw new Error(`Failed to read file data: ${conversionError.message}`);
        }
      } else {
        throw new Error('Media object must have either a data property or a uri property');
      }

      // Prepare media payload with base64 data
      const mediaPayload = {
        name: media.name,
        data: base64Data,
        type: media.type || 'application/octet-stream',
        metadata: {
          ...(media.metadata || {}),
          // Include user-provided metadata
          title: metadata?.title || media.name,
          description: metadata?.description || '',
          location: metadata?.location || '',
          uploadedVia: 'mediaScreen',
          uploadDate: new Date().toISOString()
        }
      };

      // Upload the media asset - backend now returns array of uploaded file objects with metadata
      const result = await uploadGlobalAsset(mediaPayload);

      if (result && Array.isArray(result) && result.length > 0) {
        const uploadedFile = result[0];
        console.log('✅ Upload successful:', {
          name: uploadedFile.name,
          size: uploadedFile.size,
          type: uploadedFile.type,
          url: uploadedFile.url
        });

        // Show success message
        await showAlert(
          'Upload Successful',
          `${uploadedFile.name || media.name} has been uploaded successfully!`
        );

        // Refresh the media library to show the new upload with metadata
        await refetch();
      } else {
        console.error('❌ Upload failed: Invalid response', result);
        await showAlert(
          'Upload Error',
          'Failed to upload media. The server returned an invalid response. Please try again.'
        );
      }
    } catch (error: any) {
      console.error('❌ Upload error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to upload media. Please check your connection and try again.';
      await showAlert('Upload Failed', errorMessage);
    } finally {
      setIsUploading(false);
    }
  }, [isUploading, refetch, showAlert]);

  // Show picker options
  const showMediaPicker = useCallback(async () => {
    console.log('🔘 Upload button clicked, showMediaPicker called');

    if (isUploading) {
      console.log('⚠️ Upload already in progress');
      await showAlert('Upload in Progress', 'Please wait for the current upload to complete.');
      return;
    }

    console.log('📋 Showing media picker dialog');

    // First ask if they want to upload Photos & Videos
    const uploadPhotosVideos = await showConfirm(
      'Upload Media',
      'Would you like to upload Photos & Videos? Click Cancel to upload Documents instead.',
      {
        confirmText: 'Photos & Videos',
        cancelText: 'Documents'
      }
    );

    try {
      if (uploadPhotosVideos) {
        // Photos & Videos selected
        const media = await pickImage();
        if (media) {
          // Set pending media and show metadata modal
          setPendingUploadMedia(media);
          setUploadMetadata({
            title: media.name || '',
            description: '',
            location: ''
          });
          setShowUploadMetadataModal(true);
        }
      } else {
        // Documents selected
        console.log('📄 Documents selected');
        const document = await pickDocument();
        if (document) {
          // Set pending media and show metadata modal
          setPendingUploadMedia(document);
          setUploadMetadata({
            title: document.name || '',
            description: '',
            location: ''
          });
          setShowUploadMetadataModal(true);
        }
      }
    } catch (error) {
      console.error('Error in media picker:', error);
      await showAlert('Error', 'Failed to pick media. Please try again.');
    }
  }, [isUploading, pickImage, pickDocument, showAlert, showConfirm]);

  // Handle metadata modal confirmation
  const handleMetadataConfirm = useCallback(async () => {
    if (!pendingUploadMedia) return;

    setShowUploadMetadataModal(false);
    const mediaToUpload = pendingUploadMedia;
    setPendingUploadMedia(null);

    // Upload with metadata
    await uploadMedia(mediaToUpload, {
      title: uploadMetadata.title.trim() || mediaToUpload.name,
      description: uploadMetadata.description.trim(),
      location: uploadMetadata.location.trim()
    });
  }, [pendingUploadMedia, uploadMetadata, uploadMedia]);

  // Handle metadata modal cancellation
  const handleMetadataCancel = useCallback(() => {
    setShowUploadMetadataModal(false);
    setPendingUploadMedia(null);
    setUploadMetadata({
      title: '',
      description: '',
      location: ''
    });
  }, []);

  // YouTube videos query
  const { data: youtubeData, isLoading: youtubeLoading, isFetching: youtubeFetching, error: youtubeError, refetch: refetchYoutube } = useQuery({
    queryKey: ['youtubeVideos'],
    queryFn: async () => {
      console.log('🎬 YouTube videos queryFn called');
      console.log('🔍 Query conditions:', {
        hasYoutubeIntegration,
        selectedTab,
        enabled: hasYoutubeIntegration && selectedTab === 'youtube'
      });

      try {
        if (!hasYoutubeIntegration) {
          console.warn('⚠️ No YouTube integration found');
          return [];
        }

        console.log('📡 Calling getYoutubeVideos API...');
        const result = await apis.getYoutubeVideos();
        console.log('✅ YouTube videos API response:', {
          hasResult: !!result,
          hasItems: !!result?.items,
          itemsLength: result?.items?.length || 0,
          resultType: typeof result,
          resultKeys: result ? Object.keys(result) : []
        });

        const items = result?.items || [];
        console.log(`📊 Returning ${items.length} YouTube videos`);
        return items;
      } catch (error: any) {
        console.error('❌ Error fetching YouTube videos:', {
          error,
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
          statusText: error?.response?.statusText
        });
        return [];
      }
    },
    enabled: hasYoutubeIntegration && selectedTab === 'youtube',
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1,
    placeholderData: [],
  });

  // Refetch YouTube videos when switching to YouTube tab
  useEffect(() => {
    if (selectedTab === 'youtube' && hasYoutubeIntegration && !youtubeLoading) {
      console.log('🔄 YouTube tab selected, ensuring videos are loaded...');
      // Only refetch if we don't have data or if there was an error
      if (!youtubeData || (Array.isArray(youtubeData) && youtubeData.length === 0)) {
        refetchYoutube();
      }
    }
  }, [selectedTab, hasYoutubeIntegration, youtubeLoading, youtubeData, refetchYoutube]);

  // Debug logging for YouTube data
  useEffect(() => {
    if (selectedTab === 'youtube') {
      console.log('📺 YouTube tab selected - Data state:', {
        hasYoutubeIntegration,
        youtubeLoading,
        youtubeFetching,
        youtubeError: youtubeError ? {
          message: youtubeError.message,
          response: (youtubeError as any)?.response?.data
        } : null,
        youtubeDataLength: Array.isArray(youtubeData) ? youtubeData.length : 'not an array',
        youtubeDataType: typeof youtubeData,
        youtubeData: youtubeData ? (Array.isArray(youtubeData) ? `Array(${youtubeData.length})` : JSON.stringify(youtubeData).substring(0, 100)) : 'null/undefined'
      });
    }
  }, [selectedTab, youtubeData, youtubeLoading, youtubeFetching, youtubeError, hasYoutubeIntegration]);

  // Calculate counts for each file type
  const fileCounts = React.useMemo(() => {
    if (!Array.isArray(data)) return { all: 0, images: 0, videos: 0, documents: 0 };

    const counts = {
      all: data.length,
      images: 0,
      videos: 0,
      documents: 0
    };

    data.forEach(item => {
      if (!item || !(typeof item === "string" || item?.url)) return;
      const fileType = getFileType(item);
      if (fileType === 'image') counts.images++;
      else if (fileType === 'video') counts.videos++;
      else if (fileType === 'document') counts.documents++;
    });

    return counts;
  }, [data, getFileType]);

  const stats = [
    {
      label: "All",
      value: 'all',
      count: fileCounts.all
    },
    {
      label: "Images",
      value: 'images',
      icon: <FileImageOutlined />,
      count: fileCounts.images
    },
    {
      label: "Videos",
      value: 'videos',
      icon: <VideoCameraOutlined />,
      count: fileCounts.videos
    },
    {
      label: "Documents",
      value: 'documents',
      icon: <FileTextOutlined />,
      count: fileCounts.documents
    }
  ];

  // Memoize the filtered data to prevent recalculation on every render
  const filteredData = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    let items = data.filter(item => item && (typeof item === "string" || item?.url));

    // Apply type filter based on selectedIndex
    const filterValue = stats[selectedIndex]?.value;
    if (filterValue && filterValue !== 'all') {
      items = items.filter(item => {
        const fileType = getFileType(item);

        if (filterValue === 'images') {
          return fileType === 'image';
        } else if (filterValue === 'videos') {
          return fileType === 'video';
        } else if (filterValue === 'documents') {
          return fileType === 'document';
        }

        return true;
      });
    }

    // Apply search filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter(item => {
        // Search in multiple fields for better results
        let searchableText = '';

        if (typeof item === 'string') {
          // For string URLs, extract filename
          searchableText = item.substring(item.lastIndexOf('/') + 1).toLowerCase();
        } else {
          // Search in name, title, filename, description, and URL
          const name = (item?.name || item?.title || item?.filename || '').toLowerCase();
          const description = (item?.description || '').toLowerCase();
          const url = (item?.url || '').toLowerCase();
          const tags = Array.isArray(item?.tags) ? item.tags.join(' ').toLowerCase() : '';

          searchableText = `${name} ${description} ${url} ${tags}`.trim();
        }

        return searchableText.includes(q);
      });
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let cutoffDate: Date;

      switch (dateFilter) {
        case 'today':
          cutoffDate = today;
          break;
        case 'week':
          cutoffDate = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoffDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          cutoffDate = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoffDate = new Date(0);
      }

      items = items.filter(item => {
        if (typeof item === 'string') return false; // Can't filter by date for string URLs
        const itemDate = new Date(item.createdAt || item.uploadedAt || item.created || item.uploaded || 0);
        return itemDate >= cutoffDate;
      });
    }

    // Apply size filter
    if (sizeFilter !== 'all') {
      items = items.filter(item => {
        if (typeof item === 'string') return false; // Can't filter by size for string URLs
        const size = parseInt(item?.size || '0');

        switch (sizeFilter) {
          case 'small':
            return size > 0 && size < 1024 * 1024; // < 1MB
          case 'medium':
            return size >= 1024 * 1024 && size < 10 * 1024 * 1024; // 1MB - 10MB
          case 'large':
            return size >= 10 * 1024 * 1024; // >= 10MB
          default:
            return true;
        }
      });
    }

    // Apply sorting
    items.sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'date') {
        const getDate = (item: any) => {
          if (typeof item === 'string') return 0;
          return new Date(item.createdAt || item.uploadedAt || item.created || item.uploaded || 0).getTime();
        };
        comparison = getDate(a) - getDate(b);
      } else if (sortBy === 'size') {
        const getSize = (item: any) => {
          if (typeof item === 'string') return 0;
          return parseInt(item?.size || '0');
        };
        comparison = getSize(a) - getSize(b);
      } else if (sortBy === 'name') {
        const getName = (item: any) => {
          if (typeof item === 'string') {
            return item.substring(item.lastIndexOf('/') + 1).toLowerCase();
          }
          return (item?.name || item?.title || item?.filename || item?.url || '').toLowerCase();
        };
        const nameA = getName(a);
        const nameB = getName(b);
        comparison = nameA.localeCompare(nameB);
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return items;
  }, [data, search, selectedIndex, getFileType, dateFilter, sizeFilter, sortBy, sortOrder]);

  // Filter YouTube videos based on search
  const filteredYouTubeData = React.useMemo(() => {
    if (!Array.isArray(youtubeData)) return [];

    if (!search.trim()) {
      return youtubeData;
    }

    const q = search.trim().toLowerCase();
    return youtubeData.filter(item => {
      // Search in title, description, and channel title
      const title = (item?.snippet?.title || '').toLowerCase();
      const description = (item?.snippet?.description || '').toLowerCase();
      const channelTitle = (item?.snippet?.channelTitle || '').toLowerCase();
      const videoId = (item?.id?.videoId || item?.contentDetails?.videoId || '').toLowerCase();

      const searchableText = `${title} ${description} ${channelTitle} ${videoId}`.trim();
      return searchableText.includes(q);
    });
  }, [youtubeData, search]);

  // Calculate metadata statistics for display
  const metadataStats = React.useMemo(() => {
    if (!data || data.length === 0) return null;

    const totalItems = data.length;
    const itemsWithMetadata = data.filter(item =>
      typeof item === 'object' && item?.size !== undefined
    ).length;
    const totalSize = data.reduce((acc, item) => {
      if (typeof item === 'object' && item?.size) {
        return acc + (parseInt(item.size) || 0);
      }
      return acc;
    }, 0);

    return {
      total: totalItems,
      withMetadata: itemsWithMetadata,
      totalSize: totalSize,
      formatted: totalSize > 1024 * 1024 * 1024
        ? `${(totalSize / (1024 * 1024 * 1024)).toFixed(2)} GB`
        : totalSize > 1024 * 1024
          ? `${(totalSize / (1024 * 1024)).toFixed(2)} MB`
          : `${(totalSize / 1024).toFixed(2)} KB`
    };
  }, [data]);
  return (
    <ErrorBoundary>
      <DefaultScreen style={{ paddingRight: Platform.select({ web: 30, default: 0 }), paddingTop: 20, flex: 1, gap: 24 }} containerStyle={{ flex: 1, paddingTop: 20 }}>
        {/* <XStack
            flex={3}
            gap={24}
            width="100%"
            height={370}
            maxHeight={370}
            alignItems="stretch"
          >
            <Animatable.View
              animation="fadeInLeft"
              duration={800}
              delay={200}
              style={{
                flex: 1,
                minWidth: 300,
                minHeight: 200,
                backgroundColor: theme ? "#ffffff" : "#1e293b",
                borderRadius: 20,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: theme ? 0.1 : 0.3,
                shadowRadius: 20,
                elevation: 10,
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
                overflow: 'hidden'
              }}
            >
              <View style={{
                padding: 20,
                borderBottomWidth: 1,
                borderBottomColor: theme ? "#e2e8f0" : "#334155"
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{
                    backgroundColor: theme ? "#ddd6fe" : "#312e81",
                    borderRadius: 8,
                    padding: 8,
                    marginRight: 12
                  }}>
                    <MaterialIcons
                      name="palette"
                      size={20}
                      color={theme ? "#7c3aed" : "#a78bfa"}
                    />
                  </View>
                  <H4 style={{
                    color: theme ? "#1e293b" : "#f1f5f9",
                    fontWeight: '700'
                  }}>
                    Branding
                  </H4>
                </View>
                <Text style={{
                  color: theme ? "#64748b" : "#94a3b8",
                  fontSize: 14,
                  marginTop: 4
                }}>
                  Customize your brand identity
                </Text>
              </View>

              <YStack padding={20} gap={12}>
                <RowComponent
                  title="Logo"
                  subtitle="Upload your company logo"
                  onPress={() => console.log("Logo pressed")}
                />
                <RowComponent
                  title="Colors"
                  subtitle="Manage your brand colors"
                  onPress={() => console.log("Colors pressed")}
                />
                <RowComponent
                  title="Font"
                  subtitle="Upload your brand font"
                  onPress={() => console.log("Font pressed")}
                />
              </YStack>
            </Animatable.View>

            <Animatable.View
              animation="fadeInLeft"
              duration={800}
              delay={400}
              style={{
                flex: 1,
                minWidth: 300,
                minHeight: 200,
                backgroundColor: theme ? "#ffffff" : "#1e293b",
                borderRadius: 20,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: theme ? 0.1 : 0.3,
                shadowRadius: 20,
                elevation: 10,
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
                overflow: 'hidden'
              }}
            >
              <View style={{
                padding: 20,
                borderBottomWidth: 1,
                borderBottomColor: theme ? "#e2e8f0" : "#334155"
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#064e3b",
                    borderRadius: 8,
                    padding: 8,
                    marginRight: 12
                  }}>
                    <MaterialIcons
                      name="storage"
                      size={20}
                      color={theme ? "#059669" : "#10b981"}
                    />
                  </View>
                  <H4 style={{
                    color: theme ? "#1e293b" : "#f1f5f9",
                    fontWeight: '700'
                  }}>
                    Storage
                  </H4>
                </View>
                <Text style={{
                  color: theme ? "#64748b" : "#94a3b8",
                  fontSize: 14,
                  marginTop: 4
                }}>
                  Monitor your storage usage
                </Text>
              </View>

              <YStack paddingHorizontal={20} justifyContent="center" flex={1}>
                <HomeQuickStats
                  stats={[
                    { label: "Total Storage", value: 1000 },
                    { label: "Used Storage", value: 500 }
                  ]}
                />
              </YStack>
            </Animatable.View>
          </XStack> */}
        <XStack alignSelf="stretch" flex={1} gap={24} alignItems="flex-start" height="80vh">
          {/* Left Section - Branding and Storage */}

          {/* Right Section - Media Library */}
          <Animatable.View
            animation="fadeInRight"
            duration={800}
            delay={600}
            style={{
              flex: 2,
              minWidth: "65%",
              height: "100%",
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderRadius: 20,
              borderWidth: 1,
              borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
              overflow: 'hidden'
            }}
          >
            <View style={{
              padding: 24,
              borderBottomWidth: 1,
              borderBottomColor: theme ? "#e2e8f0" : "#334155"
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  {/* Tabs */}
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 16 }}>
                    <TouchableOpacity
                      onPress={() => setSelectedTab('media')}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        backgroundColor: selectedTab === 'media'
                          ? (theme ? "#fef3c7" : "#451a03")
                          : (theme ? "#f8fafc" : "#334155"),
                        borderRadius: 8,
                        padding: 8,
                        paddingHorizontal: 12,
                        gap: 6,
                        minWidth: 150
                      }}
                    >
                      <MaterialIcons
                        name="photo-library"
                        size={20}
                        color={selectedTab === 'media'
                          ? (theme ? "#f59e0b" : "#fbbf24")
                          : (theme ? "#64748b" : "#94a3b8")
                        }
                      />
                      <Text style={{
                        color: selectedTab === 'media'
                          ? (theme ? "#1e293b" : "#f1f5f9")
                          : (theme ? "#64748b" : "#94a3b8"),
                        fontWeight: selectedTab === 'media' ? '700' : '500',
                        fontSize: 14
                      }}>
                        Media Library
                      </Text>
                    </TouchableOpacity>

                    {hasYoutubeIntegration && (
                      <TouchableOpacity
                        onPress={() => setSelectedTab('youtube')}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          backgroundColor: selectedTab === 'youtube'
                            ? "#ffebee"
                            : (theme ? "#f8fafc" : "#334155"),
                          borderRadius: 8,
                          padding: 8,
                          paddingHorizontal: 12,
                          gap: 6,
                          minWidth: 100
                        }}
                      >
                        <MaterialCommunityIcons
                          name="youtube"
                          size={20}
                          color={selectedTab === 'youtube' ? "#FF0000" : (theme ? "#64748b" : "#94a3b8")}
                        />
                        <Text style={{
                          color: selectedTab === 'youtube'
                            ? "#1e293b"
                            : (theme ? "#64748b" : "#94a3b8"),
                          fontWeight: selectedTab === 'youtube' ? '700' : '500',
                          fontSize: 14
                        }}>
                          YouTube
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    {((selectedTab === 'media' && isFetching && !isLoading) ||
                      (selectedTab === 'youtube' && youtubeFetching && !youtubeLoading)) && (
                        <ActivityIndicator
                          size="small"
                          color={theme ? "#059669" : "#10b981"}
                        />
                      )}
                    <Text style={{
                      color: theme ? "#64748b" : "#94a3b8",
                      fontSize: 14,
                      marginTop: 2
                    }}>
                      {selectedTab === 'media'
                        ? search.trim()
                          ? `${filteredData.length} of ${metadataStats?.total || data?.length || 0} files`
                          : metadataStats
                            ? `${metadataStats.total} files • ${metadataStats.formatted} total storage`
                            : 'Upload and organize your media files'
                        : search.trim()
                          ? `${filteredYouTubeData.length} of ${youtubeData?.length || 0} videos`
                          : youtubeData
                            ? `${youtubeData.length} videos from your channel`
                            : 'Your YouTube channel videos'
                      }
                    </Text>
                  </View>
                </View>
                {/* Search Bar */}
                <View style={{ flex: 1, marginLeft: 32, marginRight: 12 }}>
                  <TextInput
                    value={search}
                    onChangeText={setSearch}
                    placeholder={selectedTab === 'youtube' ? "Search YouTube videos..." : "Search media..."}
                    placeholderTextColor={theme ? '#64748b' : '#94a3b8'}
                    style={{
                      backgroundColor: theme ? '#f8fafc' : '#334155',
                      color: theme ? '#1e293b' : '#f1f5f9',
                      borderRadius: 8,
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      fontSize: 15,
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#334155',
                      minWidth: 180,
                      maxWidth: 320,
                    }}
                    clearButtonMode="while-editing"
                  />
                </View>
                <TouchableOpacity
                  onPress={(e) => {
                    e?.stopPropagation?.();
                    showMediaPicker();
                  }}
                  disabled={isUploading}
                  activeOpacity={0.7}
                  style={{
                    backgroundColor: isUploading
                      ? (theme ? "#94a3b8" : "#64748b")
                      : (theme ? "#059669" : "#10b981"),
                    borderRadius: 8,
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 100,
                    opacity: isUploading ? 0.7 : 1
                  }}
                >
                  {isUploading ? (
                    <>
                      <ActivityIndicator size="small" color="white" />
                      <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
                        Uploading...
                      </Text>
                    </>
                  ) : (
                    <>
                        <Feather name="upload" size={16} color="white" />
                        <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
                          Upload
                        </Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
            
            <YStack flex={1} padding={24}>
              {/* Filter Buttons - Only show for media tab */}
              {selectedTab === 'media' && (
                <>
                  <XStack justifyContent="center" paddingHorizontal={4} marginBottom={16}>
                    <View style={styles.buttonRow}>
                      {stats.map((item, idx) => (
                        <TouchableOpacity
                          key={`filter-${item.value}`}
                          style={[
                            styles.statButton,
                            selectedIndex === idx && styles.statButtonActive,
                          ]}
                          onPress={() => setSelectedIndex(idx)}
                        >
                          <View style={styles.buttonIcon}>
                            {item.icon}
                          </View>
                          <Text
                            style={[
                              styles.statButtonText,
                              selectedIndex === idx && styles.statButtonTextActive,
                            ]}
                          >
                            {item.label} ({item.count || 0})
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </XStack>

                  {/* Advanced Filters */}
                  <YStack gap="$3" marginBottom={16}>
                    {/* Date and Size Filters - Side by Side */}
                    <XStack gap="$4" justifyContent="center" flexWrap="wrap">
                      {/* Date Filter */}
                      <View style={styles.filterContainerCompact}>
                        <Text style={[styles.filterLabel, { color: theme ? "#64748b" : "#94a3b8" }]}>
                          Date:
                        </Text>
                        <View style={styles.filterButtonRow}>
                          {(['all', 'today', 'week', 'month', 'year'] as const).map((filter) => (
                            <TouchableOpacity
                              key={`date-${filter}`}
                              style={[
                                styles.filterButton,
                                dateFilter === filter && styles.filterButtonActive,
                              ]}
                              onPress={() => setDateFilter(filter)}
                            >
                              <Text
                                style={[
                                  styles.filterButtonText,
                                  dateFilter === filter && styles.filterButtonTextActive,
                                ]}
                              >
                                {filter === 'all' ? 'All' : filter === 'today' ? 'Today' : filter === 'week' ? 'Week' : filter === 'month' ? 'Month' : 'Year'}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>

                      {/* Size Filter */}
                      <View style={styles.filterContainerCompact}>
                        <Text style={[styles.filterLabel, { color: theme ? "#64748b" : "#94a3b8" }]}>
                          Size:
                        </Text>
                        <View style={styles.filterButtonRow}>
                          {(['all', 'small', 'medium', 'large'] as const).map((filter) => (
                            <TouchableOpacity
                              key={`size-${filter}`}
                              style={[
                                styles.filterButton,
                                sizeFilter === filter && styles.filterButtonActive,
                              ]}
                              onPress={() => setSizeFilter(filter)}
                            >
                              <Text
                                style={[
                                  styles.filterButtonText,
                                  sizeFilter === filter && styles.filterButtonTextActive,
                                ]}
                              >
                                {filter === 'all' ? 'All' : filter === 'small' ? '< 1MB' : filter === 'medium' ? '1-10MB' : '> 10MB'}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>
                    </XStack>

                    {/* Sort Options */}
                    <XStack justifyContent="center" alignItems="center" gap="$3">
                      <Text style={[styles.filterLabel, { color: theme ? "#64748b" : "#94a3b8", marginBottom: 0 }]}>
                        Sort:
                      </Text>
                      <View style={styles.filterButtonRow}>
                        {(['date', 'size', 'name'] as const).map((sort) => (
                          <TouchableOpacity
                            key={`sort-${sort}`}
                            style={[
                              styles.filterButton,
                              sortBy === sort && styles.filterButtonActive,
                            ]}
                            onPress={() => setSortBy(sort)}
                          >
                            <Text
                              style={[
                                styles.filterButtonText,
                                sortBy === sort && styles.filterButtonTextActive,
                              ]}
                            >
                              {sort === 'date' ? 'Date' : sort === 'size' ? 'Size' : 'Name'}
                            </Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                      <TouchableOpacity
                        style={[
                          styles.sortOrderButton,
                          {
                            backgroundColor: theme ? "#f8fafc" : "#334155",
                            borderColor: theme ? "#e2e8f0" : "#475569"
                          }
                        ]}
                        onPress={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                      >
                        <MaterialIcons
                          name={sortOrder === 'asc' ? 'arrow-upward' : 'arrow-downward'}
                          size={18}
                          color={theme ? "#1e293b" : "#f1f5f9"}
                        />
                      </TouchableOpacity>
                    </XStack>
                  </YStack>
                </>
              )}

              {/* Loading State */}
              {(selectedTab === 'media' ? isLoading : youtubeLoading) && (
                <YStack
                  justifyContent="center"
                  alignItems="center"
                  flex={1}
                  gap="$4"
                  paddingVertical="$8"
                >
                  <View style={{
                    backgroundColor: theme ? "#f8fafc" : "#0f172a",
                    borderRadius: 20,
                    padding: 32,
                    alignItems: 'center',
                    minWidth: 200,
                    borderWidth: 1,
                    borderColor: theme ? "#e2e8f0" : "#334155",
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.1,
                    shadowRadius: 8,
                    elevation: 4,
                  }}>
                    <ActivityIndicator
                      size="large"
                      color={theme ? "#059669" : "#10b981"}
                    />
                    <Text
                      color={theme ? "$color12" : "$color11"}
                      fontSize="$5"
                      fontWeight="600"
                      marginTop="$4"
                    >
                      {selectedTab === 'media' ? 'Loading Media Library' : 'Loading YouTube Videos'}
                    </Text>
                    <Text
                      color={theme ? "$gray11" : "$gray9"}
                      fontSize="$3"
                      marginTop="$2"
                      textAlign="center"
                    >
                      {selectedTab === 'media'
                        ? 'Fetching your media files...'
                        : 'Fetching videos from your channel...'}
                    </Text>
                  </View>
                </YStack>
              )}

              {/* Error State */}
              {(selectedTab === 'media' ? error : youtubeError) && (
                <YStack justifyContent="center" alignItems="center" flex={1}>
                  <Text color="$red10" fontSize="$3" marginBottom="$3">
                    {selectedTab === 'media' ? 'Error loading media library' : 'Error loading YouTube videos'}
                  </Text>
                  <Button size="$3" onPress={() => selectedTab === 'media' ? refetch() : refetchYoutube()}>
                    Retry
                  </Button>
                </YStack>
              )}

              {/* Empty State */}
              {selectedTab === 'media' && !isLoading && !error && (!Array.isArray(data) || data.length === 0) && (
                <YStack justifyContent="center" alignItems="center" flex={1} gap={16}>
                  <View style={{
                    backgroundColor: theme ? "#f8fafc" : "#334155",
                    borderRadius: 16,
                    padding: 24,
                    alignItems: 'center'
                  }}>
                    <MaterialIcons 
                      name="photo-library" 
                      size={48} 
                      color={theme ? "#64748b" : "#94a3b8"} 
                    />
                    <Text style={{
                      color: theme ? "#64748b" : "#94a3b8",
                      fontSize: 16,
                      marginTop: 12,
                      textAlign: 'center'
                    }}>
                      No media files yet
                    </Text>
                    <Text style={{
                      color: theme ? "#94a3b8" : "#64748b",
                      fontSize: 14,
                      marginTop: 4,
                      textAlign: 'center'
                    }}>
                      Upload your first media file to get started
                    </Text>
                  </View>
                </YStack>
              )}

              {/* YouTube Empty State */}
              {selectedTab === 'youtube' && !youtubeLoading && !youtubeError && (!Array.isArray(filteredYouTubeData) || filteredYouTubeData.length === 0) && (
                <YStack justifyContent="center" alignItems="center" flex={1} gap={16}>
                  <View style={{
                    backgroundColor: theme ? "#f8fafc" : "#334155",
                    borderRadius: 16,
                    padding: 24,
                    alignItems: 'center'
                  }}>
                    <MaterialCommunityIcons
                      name="youtube"
                      size={48}
                      color="#FF0000"
                    />
                    <Text style={{
                      color: theme ? "#64748b" : "#94a3b8",
                      fontSize: 16,
                      marginTop: 12,
                      textAlign: 'center'
                    }}>
                      No YouTube videos found
                    </Text>
                    <Text style={{
                      color: theme ? "#94a3b8" : "#64748b",
                      fontSize: 14,
                      marginTop: 4,
                      textAlign: 'center'
                    }}>
                      Videos from your connected YouTube channel will appear here
                    </Text>
                  </View>
                </YStack>
              )}

              {/* Media Grid */}
              {selectedTab === 'media' && !isLoading && !error && Array.isArray(data) && data.length > 0 && (
                <View style={{ flex: 1 }}>
                  <FlatList
                    data={filteredData}
                    renderItem={renderItem}
                    numColumns={numColumns}
                    key={`columns-${numColumns}`}
                    columnWrapperStyle={numColumns > 1 ? { 
                      justifyContent: 'space-between',
                      gap: 10,
                      marginBottom: 10,
                      paddingHorizontal: 8
                    } : undefined}
                    contentContainerStyle={{
                      padding: 16,
                      paddingBottom: 32,
                    }}
                    style={{ flex: 1 }}
                    nestedScrollEnabled={true}
                    initialNumToRender={6}
                    maxToRenderPerBatch={6}
                    windowSize={3}
                    removeClippedSubviews={Platform.OS === 'android'}
                    getItemLayout={undefined}
                    keyExtractor={(item, index) => {
                      const itemId = typeof item === 'string'
                        ? item.substring(item.lastIndexOf('/') + 1)
                        : item?.id || item?.url?.substring(item.url.lastIndexOf('/') + 1) || '';
                      return `media-${index}-${itemId}`;
                    }}
                    scrollEventThrottle={16}
                    onScrollBeginDrag={() => {
                      if (animationRef.current) {
                        cancelAnimationFrame(animationRef.current);
                      }
                    }}
                    onScrollEndDrag={() => {
                      // Resume animations after scrolling
                    }}
                  />
                </View>
              )}

              {/* YouTube Grid */}
              {selectedTab === 'youtube' && !youtubeLoading && !youtubeError && Array.isArray(filteredYouTubeData) && filteredYouTubeData.length > 0 && (
                <View style={{ flex: 1 }}>
                  <FlatList
                    showsVerticalScrollIndicator={true}
                    data={filteredYouTubeData}
                    renderItem={({ item, index }) => (
                      <View style={{
                        width: numColumns > 1 ? `${(100 / numColumns) - (10 / numColumns)}%` : '100%',
                        flexBasis: numColumns > 1 ? `${(100 / numColumns) - (10 / numColumns)}%` : '100%',
                        flexGrow: 0,
                        flexShrink: 0,
                        paddingHorizontal: numColumns > 1 ? 0 : 8,
                        marginBottom: numColumns > 1 ? 0 : 16,
                      }}>
                        <YouTubeVideoItem item={item} index={index} />
                      </View>
                    )}
                    numColumns={numColumns}
                    key={`youtube-columns-${numColumns}`}
                    columnWrapperStyle={numColumns > 1 ? {
                      justifyContent: 'space-between',
                      gap: 10,
                      marginBottom: 10,
                      paddingHorizontal: 8
                    } : undefined}
                    contentContainerStyle={{
                      padding: 16,
                      paddingBottom: 32,
                    }}
                    style={{ flex: 1 }}
                    nestedScrollEnabled={true}
                    initialNumToRender={6}
                    maxToRenderPerBatch={6}
                    windowSize={3}
                    removeClippedSubviews={Platform.OS === 'android'}
                    getItemLayout={undefined}
                    keyExtractor={(item, index) => {
                      // Handle both playlistItems format (contentDetails.videoId) and search format (id.videoId)
                      const videoId = item?.contentDetails?.videoId || item?.id?.videoId || item?.id || index;
                      return `youtube-${index}-${videoId}`;
                    }}
                    scrollEventThrottle={16}
                  />
                </View>
              )}
            </YStack>
          </Animatable.View>
        </XStack>

        {/* Image Enhancement Modal */}
        {selectedImageForEnhancement && (
          <ImageEnhancementModal
            visible={enhancementModalVisible}
            onClose={() => {
              setEnhancementModalVisible(false);
              setSelectedImageForEnhancement(null);
              setSelectedImageIndex(null);
            }}
            imageUrl={selectedImageForEnhancement}
            imageIndex={selectedImageIndex || undefined}
            onEnhancementComplete={async (enhancedImageUrl) => {
              try {
                // Convert enhanced image to base64 format for upload
                const base64Data = enhancedImageUrl.split(',')[1];
                const imagePayload = {
                  uri: enhancedImageUrl,
                  fileName: `enhanced-image-${Date.now()}.png`,
                  mimeType: 'image/png'
                };

                // Upload the enhanced image as a new media asset
                const result = await uploadGlobalAsset(imagePayload);

                if (result && Array.isArray(result) && result.length > 0) {
                  await showAlert('Success', 'Enhanced image has been uploaded to your media library');

                  // Refetch to update the media library
                  await refetch();
                } else {
                  await showAlert('Error', 'Failed to upload enhanced image. Please try again.');
                }
              } catch (error) {
                console.error('Error saving enhanced image:', error);
                await showAlert('Error', 'Failed to save enhanced image. Please try again.');
              }
            }}
          />
        )}

        {/* Upload Metadata Modal */}
        <Modal
          visible={showUploadMetadataModal}
          transparent={true}
          animationType="slide"
          onRequestClose={handleMetadataCancel}
          onDismiss={()=>{
            refetch();
          }}
        >
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={{ flex: 1 }}
          >
            <Pressable
              style={{
                flex: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              onPress={handleMetadataCancel}
            >
              <Pressable
                onPress={(e) => e.stopPropagation()}
                style={{
                  width: '90%',
                  maxWidth: 500,
                  backgroundColor: theme ? "#ffffff" : "#1e293b",
                  borderRadius: 20,
                  padding: 24,
                  borderWidth: 1,
                  borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.1)",
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.3,
                  shadowRadius: 8,
                  elevation: 10,
                }}
              >
                <YStack gap="$4">
                  <XStack alignItems="center" justifyContent="space-between" marginBottom="$2">
                    <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                      Add Media Details
                    </H4>
                    <Pressable onPress={handleMetadataCancel}>
                      <MaterialIcons name="close" size={24} color={theme ? "#64748b" : "#94a3b8"} />
                    </Pressable>
                  </XStack>

                  <Text
                    color={theme ? "$gray11" : "$gray9"}
                    fontSize="$3"
                    marginBottom="$2"
                  >
                    Add context to your media file (all fields are optional)
                  </Text>

                  <YStack gap="$3">
                    {/* Title Field */}
                    <YStack gap="$1">
                      <Text
                        color={theme ? "$color12" : "$color11"}
                        fontSize="$3"
                        fontWeight="500"
                      >
                        Title
                      </Text>
                      <TextInput
                        placeholder="Enter a title for this media"
                        placeholderTextColor={theme ? '#94a3b8' : '#64748b'}
                        value={uploadMetadata.title}
                        onChangeText={(text) => setUploadMetadata(prev => ({ ...prev, title: text }))}
                        style={{
                          backgroundColor: theme ? '#f8fafc' : '#0f172a',
                          color: theme ? '#1e293b' : '#f1f5f9',
                          borderRadius: 12,
                          paddingHorizontal: 16,
                          paddingVertical: 12,
                          fontSize: 15,
                          borderWidth: 1,
                          borderColor: theme ? '#cbd5e1' : '#475569',
                        }}
                        autoFocus
                      />
                    </YStack>

                    {/* Description Field */}
                    <YStack gap="$1">
                      <Text
                        color={theme ? "$color12" : "$color11"}
                        fontSize="$3"
                        fontWeight="500"
                      >
                        Description
                      </Text>
                      <TextInput
                        placeholder="Add a description (optional)"
                        placeholderTextColor={theme ? '#94a3b8' : '#64748b'}
                        value={uploadMetadata.description}
                        onChangeText={(text) => setUploadMetadata(prev => ({ ...prev, description: text }))}
                        multiline
                        numberOfLines={4}
                        textAlignVertical="top"
                        style={{
                          backgroundColor: theme ? '#f8fafc' : '#0f172a',
                          color: theme ? '#1e293b' : '#f1f5f9',
                          borderRadius: 12,
                          paddingHorizontal: 16,
                          paddingVertical: 12,
                          fontSize: 15,
                          borderWidth: 1,
                          borderColor: theme ? '#cbd5e1' : '#475569',
                          minHeight: 100,
                        }}
                      />
                    </YStack>

                    {/* Location Field */}
                    <YStack gap="$1">
                      <Text
                        color={theme ? "$color12" : "$color11"}
                        fontSize="$3"
                        fontWeight="500"
                      >
                        Location <Text color={theme ? "$gray10" : "$gray9"} fontWeight="400">(optional)</Text>
                      </Text>
                      <TextInput
                        placeholder="Add location (optional)"
                        placeholderTextColor={theme ? '#94a3b8' : '#64748b'}
                        value={uploadMetadata.location}
                        onChangeText={(text) => setUploadMetadata(prev => ({ ...prev, location: text }))}
                        style={{
                          backgroundColor: theme ? '#f8fafc' : '#0f172a',
                          color: theme ? '#1e293b' : '#f1f5f9',
                          borderRadius: 12,
                          paddingHorizontal: 16,
                          paddingVertical: 12,
                          fontSize: 15,
                          borderWidth: 1,
                          borderColor: theme ? '#cbd5e1' : '#475569',
                        }}
                      />
                    </YStack>
                  </YStack>

                  {/* Action Buttons */}
                  <XStack gap="$3" marginTop="$4">
                    <Button
                      flex={1}
                      size="$4"
                      variant="outlined"
                      onPress={handleMetadataCancel}
                      backgroundColor={theme ? "#f8fafc" : "#334155"}
                      borderColor={theme ? "#cbd5e1" : "#475569"}
                    >
                      <Text
                        color={theme ? "$color12" : "$color11"}
                        fontWeight="600"
                      >
                        Cancel
                      </Text>
                    </Button>
                    <Button
                      flex={1}
                      size="$4"
                      theme="active"
                      onPress={handleMetadataConfirm}
                      backgroundColor={theme ? "#059669" : "#10b981"}
                    >
                      <Text
                        color="white"
                        fontWeight="600"
                      >
                        Upload
                      </Text>
                    </Button>
                  </XStack>
                </YStack>
              </Pressable>
            </Pressable>
          </KeyboardAvoidingView>
        </Modal>

        {/* Upload Loading Overlay */}
        {isUploading && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <View
              style={{
                backgroundColor: theme ? "#ffffff" : "#1e293b",
                borderRadius: 16,
                padding: 32,
                alignItems: 'center',
                minWidth: 200,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.3,
                shadowRadius: 8,
                elevation: 10,
              }}
            >
              <ActivityIndicator
                size="large"
                color={theme ? "#059669" : "#10b981"}
              />
              <Text
                style={{
                  color: theme ? "#1e293b" : "#f1f5f9",
                  fontSize: 16,
                  fontWeight: '600',
                  marginTop: 16,
                }}
              >
                Uploading...
              </Text>
              <Text
                style={{
                  color: theme ? "#64748b" : "#94a3b8",
                  fontSize: 14,
                  marginTop: 8,
                  textAlign: 'center',
                }}
              >
                Please wait while your file is being uploaded
              </Text>
            </View>
          </View>
        )}
      </DefaultScreen>
    </ErrorBoundary>
  );
};

const styles = StyleSheet.create({
  // Media item styles
  mediaItemContainer: {
    flex: 1,
    width: '100%',
  },
  mediaImage: {
    flex: 1,
    width: '90%',
    maxWidth: 300,
    resizeMode:'contain',
    height: 'auto',
    maxHeight: 300,
    minHeight: 200,
    borderRadius: 8,
  },
  mediaPlaceholder: {
    flex: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },

  // Card styles
  card: {
    height: '100%',
    borderRadius: 8,
    borderWidth: 0,
  },
  cardBody: {
    padding: 24,
  },

  // Text styles
  title: {
    marginBottom: 2,
    fontWeight: 'bold' as const,
    fontSize: 18,
  },

  // Stats section
  statSection: {
    marginTop: 24,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    marginBottom: 44,
  },
  statInfo: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  },
  statLabel: {
    color: '#6c757d',
    fontWeight: '400' as const,
    marginBottom: 12,
    fontSize: 16,
  },
  statValue: {
    fontWeight: '600' as const,
    fontSize: 20,
    marginBottom: 0,
  },

  // Chart container
  chartContainer: {
    position: 'relative' as const,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  },

  // Button styles
  buttonRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    marginTop: 8,
    flexWrap: 'wrap' as const,
    gap: 8,
  },
  statButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    backgroundColor: '#f0f0f0',
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 6,
    minWidth: 80,
    justifyContent: 'center' as const,
  },
  buttonIcon: {
    width: 16,
    height: 16,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  },
  statButtonActive: {
    backgroundColor: '#487fff',
    shadowColor: '#487fff',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 3,
  },
  statButtonText: {
    color: '#333',
    fontWeight: '500' as const,
    fontSize: 14,
  },
  statButtonTextActive: {
    color: '#fff',
  },

  // Upload button styles
  uploadButton: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#487fff',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 44,
  },
  uploadButtonText: {
    color: 'white',
    fontWeight: '600' as const,
    fontSize: 16,
  },

  // Filter container styles
  filterContainer: {
    marginBottom: 12,
    width: '100%',
    maxWidth: 400,
  },
  filterContainerCompact: {
    flex: 1,
    minWidth: 200,
    maxWidth: 400,
  },
  filterLabel: {
    fontSize: 11,
    fontWeight: '600' as const,
    marginBottom: 6,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  filterButtonRow: {
    flexDirection: 'row' as const,
    gap: 6,
    flexWrap: 'wrap' as const,
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  filterButtonActive: {
    backgroundColor: '#487fff',
    borderColor: '#487fff',
  },
  filterButtonText: {
    color: '#333',
    fontWeight: '500' as const,
    fontSize: 12,
  },
  filterButtonTextActive: {
    color: '#fff',
    fontWeight: '600' as const,
  },
  sortOrderButton: {
    padding: 6,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minWidth: 32,
    minHeight: 32,
  },
});

export default MediaScreen;
